import React, { memo, useState } from 'react';
import { toast } from 'react-toastify';
import { useObservable } from 'react-use-observable';

import { DislikeFilled as DeslikeSolid, LikeFilled as LikeSolid } from '@ant-design/icons';
import { Button, Checkbox, Table, Tooltip } from 'antd';

import Alert from 'components/Alert';
import Truncate from 'components/Truncate';
import { formatDate } from 'helpers/date';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import TableOptions from 'v2/components/Shared/TableOptions';
import affiliateService from 'v2/services/affiliate';

interface IProps {
  onSort: (sortField: string, sortDirection: 'asc' | 'desc') => void;
}

const TablePending = ({ onSort }: IProps) => {
  const [isLoading] = useObservable(() => affiliateService.isLoading(), []);
  const [affiliates] = useObservable(() => affiliateService.getAffiliates(), []);
  const [pagination] = useObservable(() => affiliateService.getPaginationResponse(), []);
  const [currentSort] = useObservable(() => affiliateService.getCurrentSort(), []);

  const [affiliatesSelected, setAffiliatesSelected] = useState([]);

  const resetAffiliatesSelected = () => setAffiliatesSelected([]);

  const onChangeRowsPerPage = (perPage: number) => {
    affiliateService.setPagination({ ...pagination, ...{ per_page: perPage, current_page: 1 } });
  };

  const onChangePage = (page: number) => {
    affiliateService.setPagination({ ...pagination, ...{ current_page: page } });
  };

  const selectAllAffiliates = () => {
    const selected = affiliates.map(a => a.id);

    if (affiliates.length > affiliatesSelected.length) {
      setAffiliatesSelected(selected);
      return;
    }

    setAffiliatesSelected([]);
  };

  const toggleSelect = (id: number) => {
    if (affiliatesSelected.includes(id)) {
      setAffiliatesSelected(affiliatesSelected.filter(a => a !== id));
      return;
    }

    setAffiliatesSelected([...affiliatesSelected, id]);
  };

  const handleClickModerate = (id: number, status: 'A' | 'R') => {
    affiliateService.moderateAffiliate(id, status).subscribe(() => {
      setAffiliatesSelected([]);
      toast.success(`Afiliado ${status === 'A' ? 'aprovado' : 'reprovado'} com sucesso.`);
    }, toast.error);
  };

  const moderateAffiliates = async (status: 'A' | 'R') => {
    const confirmation = await Alert.show({
      title: 'Atenção',
      message: `Deseja realmente ${status === 'A' ? 'aprovar' : 'reprovar'} os afiliados selecionados?`,
      confirmation: true,
      buttonOk: status === 'A' ? 'Aprovar' : 'Reprovar',
      buttonCancel: 'Cancelar'
    });
    if (!confirmation) return;

    affiliateService.moderateMultipleAffiliates(affiliatesSelected, status).subscribe(() => {
      setAffiliatesSelected([]);
      toast.success(`Afiliados ${status === 'A' ? 'aprovados' : 'reprovados'} com sucesso.`);
    }, toast.error);
  };

  const handleSortClick = (field: string) => {
    const isAsc = currentSort?.field === field && currentSort?.direction === 'asc';
    onSort(field, isAsc ? 'desc' : 'asc');
  };

  const columns: any[] = [
    {
      title: () => (
        <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
          <Checkbox
            indeterminate={!!affiliatesSelected?.length && affiliatesSelected?.length < affiliates?.length}
            checked={!!affiliates?.length && affiliatesSelected?.length === affiliates?.length}
            onChange={selectAllAffiliates}
            disabled={!affiliates?.length}
          />
        </PermissionHide>
      ),
      width: 45,
      render: (_: any, affiliate: any) => (
        <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
          <Checkbox checked={affiliatesSelected.includes(affiliate.id)} onClick={() => toggleSelect(affiliate.id)} />
        </PermissionHide>
      )
    },
    {
      title: 'Data da solicitação',
      width: 170,
      render: (_: any, affiliate: any) => <span>{formatDate(affiliate.request_date)}</span>,
      onHeaderCell: () => ({ onClick: () => handleSortClick('request_date') })
    },
    {
      title: 'Produto',
      width: 300,
      render: (_: any, affiliate: any) => (
        <>
          <span className='text-xs font-semibold'>
            <Truncate count={30} final='...'>
              {affiliate.product_title}
            </Truncate>
          </span>
          <span className='text-xs'>({affiliate.product_id})</span>
        </>
      ),
      onHeaderCell: () => ({ onClick: () => handleSortClick('product_title') })
    },
    {
      title: 'Afiliado',
      render: (_: any, affiliate: any) => (
        <span>
          <a href={`/producer/detail/${affiliate.affiliate_id}`} target='_blank' rel='noopener noreferrer'>
            <Truncate count={25} final={'...'}>
              {affiliate.affiliate_name}
            </Truncate>
          </a>
          {affiliate.affiliate_email}
        </span>
      ),
      onHeaderCell: () => ({ onClick: () => handleSortClick('affiliate_name') })
    },
    {
      title: 'Mensagem',
      width: 450,
      render: (_: any, affiliate: any) => (
        <Truncate count={50} final='...'>
          {affiliate.request_message || '-'}
        </Truncate>
      )
    },
    {
      title: 'Ações',
      width: 130,
      fixed: 'right',
      align: 'center' as const,
      render: (_: any, affiliate: any) => (
        <PermissionHide action={'myeduzz:meus-afiliados:editar'}>
          <Tooltip title='Aprovar' placement='top'>
            <Button type='text' icon={<LikeSolid />} onClick={() => handleClickModerate(affiliate.id, 'A')} />
          </Tooltip>
          <Tooltip title='Reprovar' placement='top'>
            <Button type='text' icon={<DeslikeSolid />} onClick={() => handleClickModerate(affiliate.id, 'R')} />
          </Tooltip>
        </PermissionHide>
      )
    }
  ];

  return (
    <>
      {!!affiliatesSelected.length && (
        <div className='relative mb-[70px] max-md:mb-[100px]'>
          <TableOptions
            items={affiliatesSelected.length}
            style={{ top: 0, padding: 16 }}
            options={[
              { label: 'Cancelar', callback: () => resetAffiliatesSelected() },
              { icon: 'thumb_down', label: 'Reprovar', callback: () => moderateAffiliates('R') },
              { icon: 'thumb_up', label: 'Aprovar', color: 'primary', callback: () => moderateAffiliates('A') }
            ]}
          />
        </div>
      )}

      <div>
        <Table
          id='affiliates_table'
          loading={isLoading}
          columns={columns}
          dataSource={affiliates || []}
          scroll={{ x: 'max-content' }}
          rowKey='id'
          pagination={
            pagination
              ? {
                  current: pagination?.current_page || 1,
                  pageSize: pagination?.per_page || 10,
                  total: pagination?.total_items || 0,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100', '200'],
                  onChange: page => onChangePage(page),
                  onShowSizeChange: (_, size) => onChangeRowsPerPage(size)
                }
              : false
          }
        />
      </div>
    </>
  );
};

export default memo(TablePending);
